/**
 * Класс для работы с мета-данными JSON-RPC запросов и ответов
 * Содержит служебную информацию о сессии, пагинации, уведомлениях и времени выполнения
 */
import Serializable      from "@/core/misc/mixins/Serializable";
import RpcMetaTime       from "@/core/services/Rpc/RpcMetaTime";
import TimeZone          from "@/domains/TimeZones/TimeZone";
import RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import RpcDataSort       from "@/core/services/Rpc/RpcDataSort";
import Utils             from "@/core/utils/Utils";
import Core              from "@/core/Core";
import type RpcDebug     from "@/core/services/Rpc/RpcDebug";

class RpcMeta extends Serializable {
    /**
     * Токен текущей сессии пользователя
     */
    sessionToken: string | null = null;

    /**
     * Настройки пагинации для списочных запросов
     */
    pagination: RpcDataPagination | null;

    /**
     * Настройки сортировки для списочных запросов
     */
    sort: RpcDataSort | null;

    /**
     * Информация о времени выполнения запроса
     * null — для несостоявшихся запросов (отмена, таймаут, транспортная ошибка)
     */
    time: RpcMetaTime | null;

    /**
     * Trace ID для связи frontend и backend ошибок
     */
    sentryTraceId: string | null = null;

    /**
     * Уведомления от сервера для отображения пользователю
     * Обычно содержат предупреждения из тестовой среды
     */
    notifications: string[] | null = null;

    /**
     * Debug-режим.
     * boolean — включён/выключен (основной RpcService)
     * Record — конкретные флаги (DevTools)
     * null — не передаётся
     */
    debug: Record<string, boolean> | boolean | null = null;

    static fromRequest(sessionToken: string | null, pagination: Partial<RpcDataPagination> | null, sort: Partial<RpcDataSort> | null, timeZone: TimeZone): RpcMeta {
        const obj = new this();

        obj.sessionToken  = sessionToken;
        obj.pagination    = RpcDataPagination.fromRequest(pagination);
        obj.sort          = RpcDataSort.fromRequest(sort);
        obj.time          = RpcMetaTime.fromRequest(timeZone);
        obj.sentryTraceId = this.#generateTraceId();

        if (Core.debugMode.value === true && Core.config.isDev === true) { obj.debug = true; }

        return obj;
    }

    static fromServer(data: any): RpcMeta {
        const obj = super.fromServer(data) as this;

        if (Utils.isObject(data.pagination)) { obj.pagination = RpcDataPagination.fromServer(data.pagination); }
        if (Utils.isObject(data.sort))       { obj.sort       = RpcDataSort.fromServer(data.sort); }
        obj.time = RpcMetaTime.fromServer(data.time);

        return obj;
    }

    static fromDevTools(
        sessionToken: string | null,
        pagination:   RpcDataPagination,
        sort:         RpcDataSort,
        debug:        RpcDebug,
        timeZone:     TimeZone,
    ): RpcMeta {
        const obj = new this();

        obj.sessionToken  = sessionToken;
        obj.pagination    = RpcDataPagination.fromRequest(pagination);
        obj.sort          = RpcDataSort.fromRequest(sort);
        obj.time          = RpcMetaTime.fromRequest(timeZone);
        obj.sentryTraceId = null;
        obj.debug         = debug.toRequest();

        return obj;
    }

    /**
     * Пустая мета для несостоявшихся запросов (отмена, таймаут, транспортная ошибка).
     * Гарантирует инвариант: response.meta существует всегда.
     */
    static createEmpty(): RpcMeta {
        const obj = new this();

        obj.pagination = null;
        obj.sort       = null;
        obj.time       = null;

        return obj;
    }

    static #generateTraceId(): string {
        return crypto.randomUUID();
    }

    toSentryContext(): any {
        const sanitized: RpcMeta = { ...this };

        sanitized.sessionToken = '{{sessionToken}}';

        return sanitized;
    }
}

export default RpcMeta;